import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { User } from "types";

export type EmergencyContact = {
  name?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
};

export type PersonalInfo = {
  nationality?: string;
  passportNo?: string;
  emergencyContact?: EmergencyContact;
  dateOfBirth?: string;
  phoneNumber?: string;
};

export type BankInfo = {
  bankName?: string;
  accountNo?: string;
};

export type EmploymentType =
  | "full-time"
  | "part-time"
  | "contract"
  | "internship"
  | string;

// NEW: Attendance configuration types (matching backend structure)
export type WorkingHoursChild = {
  date: string;
  startTime: string;
  endTime: string;
  totalHours: number;
};

export type RecurrenceType = "day" | "weekly" | "biweekly" | "monthly";

export type WorkingHoursConfig = {
  type: string;
  date?: string;
  recurringenabled?: boolean;
  recurrenceType?: RecurrenceType;
  monthlyPattern?: string;
  interval?: number;
  selectedDays?: string[];
  startTime: string;
  endTime: string;
  totalHours: number;
  child?: WorkingHoursChild[];
};

export type OvertimeConfig = {
  allowed: boolean;
  maxHoursPerDay?: number;
  maxHoursPerMonth?: number;
  overtimeRate?: number;
  requiresApproval: boolean;
};

export type LateArrivalConfig = {
  graceMinutes: number;
  markAsHalfDayAfter?: number;
  deductionEnabled?: boolean;
  deductMinutesFor?: number;
  forEveryMinutes?: number;
};

export type EarlyDepartureConfig = {
  graceMinutes?: number;
  markHalfDayBeforeMinutes?: number;
};

export type BreakConfig = {
  type?: "duration" | "timeRange";
  durationHours?: number;
  startTime?: string;
  endTime?: string;
};

export type LocationConfig = {
  lat: number | string;
  lng: number | string;
};

export type UpdateProfilePayload = {
  firstName?: string;
  lastName?: string;
  position?: string;
  description?: string;
  profileImage?: string;
  treatyNo?: string;
  corporateEmail?: string;
  corporatePhone?: string;
  reportingManager?: string;
  employmentType?: EmploymentType;
  employmentCategory?: string;
  inactiveReason?: string;
  startDate?: string;
  endDate?: string;
  personalInfo?: PersonalInfo;
  bankInfo?: BankInfo;
  // NEW: Attendance configuration fields
  workingHours?: WorkingHoursConfig[];
  overtimeConfig?: OvertimeConfig;
  lateArrivalConfig?: LateArrivalConfig;
  earlyDepartureConfig?: EarlyDepartureConfig;
  break?: BreakConfig;
  location?: LocationConfig;
  isLocationCheck?: boolean;
  timezone?: string;
};

export type UpdateProfileResponse = {
  success: boolean;
  message: string;
  data: User;
};

const updateUserProfile = async (
  id: string,
  payload: UpdateProfilePayload
): Promise<UpdateProfileResponse> => {
  // Remove undefined and empty string fields from payload, but keep null values
  // (null is needed to explicitly unset fields like break, overtimeConfig, etc.)
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([_, value]) => value !== undefined && value !== '')
  ) as UpdateProfilePayload;

  const { data } = await customAxios.patch<UpdateProfileResponse>(
    `/users/${id}/profile`,
    cleanPayload
  );
  return data;
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateProfileResponse,
    AxiosError,
    { id: string; payload: UpdateProfilePayload }
  >(({ id, payload }) => updateUserProfile(id, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "Profile updated successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["users"]);
      await queryClient.invalidateQueries(["user", variables.id]);
      await queryClient.invalidateQueries(["userProfile", variables.id]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update profile",
        color: "red",
      }),
  });
};
